// Core
import { useMemo } from "react";
import { DateTime } from "luxon";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Components
import { Button, Chip, Crumbs, DataTable, KpiStrip, PageHead } from "components/AtomsV2";
import { Icon } from "components/ShellV2";
import HelpFloatingButtonV2 from "components/HelpFloatingButtonV2";

// Redux
import { useDispatch, useSelector } from "react-redux";
import { openNotificationPopup, openWarningAlert } from "state/application/applicationSlice";
import { selectCurrentCommunity } from "state/communities/communitiesSlice";
import {
  selectAllResources,
  useDeleteResourceByIdMutation,
  useGetResourcesByCommunityIdQuery,
} from "state/resources/resourcesApiSlice";
import { selectAllMembers, useGetMembersByCommunityIdQuery } from "state/members/membersApiSlice";

// Utils
import { resourceVisual } from "utils/resourceTypes";
import { countMembersWithResourceId } from "./utils";

function ListResources() {
  const community = useSelector(selectCurrentCommunity);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation(["resources", "community"]);

  // READ Resources
  const { resources } = useGetResourcesByCommunityIdQuery(
    { communityId: community.id, excludeDeleted: "true" },
    {
      selectFromResult: ({ data }) => ({ resources: (data && selectAllResources(data)) || [] }),
    }
  );

  // READ Members (for member count per resource)
  const { members } = useGetMembersByCommunityIdQuery(
    { communityId: community.id, excludeDeleted: "true" },
    {
      selectFromResult: ({ data: membersData }) => ({
        members: membersData && selectAllMembers(membersData),
      }),
    }
  );

  // DELETE Resource
  const [deleteResource] = useDeleteResourceByIdMutation();
  const onDeleteResourceClicked = async (resourceId) => {
    const action = async () => {
      try {
        const response = await deleteResource({ communityId: community.id, resourceId }).unwrap();
        if (response) {
          dispatch(openNotificationPopup({ notification: response.message, type: "error" }));
        } else {
          dispatch(
            openNotificationPopup({
              notification: t("notifications.deleted_success"),
              type: "success",
            })
          );
        }
      } catch (err) {
        dispatch(openNotificationPopup({ notification: err, type: "error" }));
      }
    };
    dispatch(
      openWarningAlert({
        confirmText: t("delete.confirm_button", { ns: "community" }),
        confirmAction: action,
      })
    );
  };

  const openResource = (resourceId) =>
    navigate(`/communities/${community.id}/infrastructure/resources/${resourceId}`);

  const typeLabel = (type) => t(`new_resource.selects.types.${type}`, { defaultValue: type });

  const rows = useMemo(
    () =>
      resources.map((r) => ({
        id: r.id,
        name: r.name,
        type: r.type,
        joined: r.created_at ? DateTime.fromISO(r.created_at).toLocaleString() : "—",
        num_members: countMembersWithResourceId(members, r.id),
        total_perc: r.total_ownership ?? 0,
        status: r.status,
      })),
    [resources, members]
  );

  const columns = [
    {
      key: "name",
      label: t("table.columns.resource"),
      render: (v, row) => {
        const visual = resourceVisual(row.type);
        return (
          <div className="rec-cell-member">
            <div className={`rec-av-sm ${visual.tint}`}>
              <Icon name={visual.icon} style={{ fontSize: 18 }} />
            </div>
            <b>{v}</b>
          </div>
        );
      },
    },
    {
      key: "type",
      label: t("table.columns.type"),
      render: (v) => <Chip tone="gray">{typeLabel(v)}</Chip>,
    },
    { key: "joined", label: t("table.columns.joined") },
    { key: "num_members", label: t("table.columns.num_members"), align: "center" },
    {
      key: "total_perc",
      label: t("table.columns.total_perc_ownership"),
      align: "right",
      render: (v) => (
        <span
          className="tabular-nums font-semibold"
          style={{ color: v >= 100 ? "var(--success)" : "var(--warning)" }}
        >
          {`${v}%`}
        </span>
      ),
    },
    {
      key: "status",
      label: t("table.columns.status"),
      render: (v) =>
        v === "ACTIVE" ? (
          <Chip tone="green">{t("table.badges.active")}</Chip>
        ) : (
          <Chip tone="red">{t("table.badges.inactive")}</Chip>
        ),
    },
    {
      key: "_actions",
      label: "",
      sort: false,
      align: "right",
      render: (v, row) => (
        <div className="rec-row-actions">
          <button
            type="button"
            className="rec-icon-btn"
            title={t("table.actions.edit", { defaultValue: "Editar" })}
            onClick={(e) => {
              e.stopPropagation();
              openResource(row.id);
            }}
          >
            <Icon name="edit" />
          </button>
          <button
            type="button"
            className="rec-icon-btn"
            title={t("table.actions.delete", { defaultValue: "Remover" })}
            onClick={(e) => {
              e.stopPropagation();
              onDeleteResourceClicked(row.id);
            }}
          >
            <Icon name="delete" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <HelpFloatingButtonV2 helpScreen="resource-list" />
      <Crumbs
        items={[
          { label: community?.name || "—" },
          {
            label: t("sidenav.infrastructure", { ns: "community", defaultValue: "Infraestrutura" }),
          },
          { label: t("title") },
        ]}
      />
      <PageHead
        title={t("title")}
        subtitle={t("subtitle")}
        actions={
          <Button
            onClick={() => navigate(`/communities/${community.id}/infrastructure/resources/new`)}
          >
            <Icon name="add" />
            {t("new_button")}
          </Button>
        }
      />

      <KpiStrip
        className="mb-rec"
        cols={4}
        items={[
          {
            icon: "solar_power",
            value: String(resources.length),
            label: t("stats.total"),
            tint: "teal",
          },
          {
            icon: "wb_sunny",
            value: String(resources.filter((r) => r.type === "SCPU").length),
            label: t("stats.solar"),
            tint: "amber",
          },
          {
            icon: "battery_charging_full",
            value: String(resources.filter((r) => r.type === "STORAGE").length),
            label: t("stats.batteries"),
            tint: "green",
          },
          {
            icon: "electric_car",
            value: String(resources.filter((r) => r.type === "EV").length),
            label: t("stats.evs"),
            tint: "info",
          },
        ]}
      />

      <div className="rec-card">
        <DataTable
          columns={columns}
          rows={rows}
          onRow={(row) => openResource(row.id)}
          search={t("search_placeholder")}
          searchKeys={["name", "type"]}
        />
      </div>
    </div>
  );
}

export default ListResources;
